import { useState, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TourCard from '../../components/base/TourCard';
import PriceRangeSlider from '../../components/base/PriceRangeSlider';
import { yurtdisiTours } from '../../mocks/tours';

type SortKey = 'default' | 'price-asc' | 'price-desc' | 'name-asc';

const MIN_PRICE = 399;
const MAX_PRICE = 1099;

const DURATION_OPTIONS = [
  { label: 'Tümü', min: 0, max: 99 },
  { label: '4-5 Gün', min: 4, max: 5 },
  { label: '7-8 Gün', min: 7, max: 8 },
];

export default function YurtdisiTurlar() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [search, setSearch] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([MIN_PRICE, MAX_PRICE]);
  const [durationIdx, setDurationIdx] = useState(0);
  const [sort, setSort] = useState<SortKey>('default');

  const filtered = useMemo(() => {
    const dur = DURATION_OPTIONS[durationIdx];
    let result = yurtdisiTours.filter(t => {
      const matchSearch = t.name.toLowerCase().includes(search.toLowerCase());
      const matchPrice = t.price >= priceRange[0] && t.price <= priceRange[1];
      const matchDur = dur.min === 0 || (t.duration >= dur.min && t.duration <= dur.max);
      return matchSearch && matchPrice && matchDur;
    });
    switch (sort) {
      case 'price-asc': return [...result].sort((a, b) => a.price - b.price);
      case 'price-desc': return [...result].sort((a, b) => b.price - a.price);
      case 'name-asc': return [...result].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
      default: return result;
    }
  }, [search, priceRange, durationIdx, sort]);

  const resetFilters = () => {
    setSearch('');
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    setDurationIdx(0);
    setSort('default');
  };

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${siteUrl}/yurtdisi-turlar`,
    url: `${siteUrl}/yurtdisi-turlar`,
    name: 'Ankara Çıkışlı Yurt Dışı Turlar',
    description: 'Ankara çıkışlı Avrupa ve dünya tur paketleri. Paris, Roma, Barcelona, Dubai ve daha fazla destinasyon.',
    inLanguage: 'tr-TR',
    numberOfItems: yurtdisiTours.length,
    itemListElement: yurtdisiTours.slice(0, 10).map((tour, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: tour.name,
      url: `${siteUrl}/tur/${tour.id}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <title>Yurt Dışı Turlar Ankara Çıkışlı | Avrupa ve Dünya Turları</title>
      <meta name="description" content="Ankara çıkışlı yurt dışı tur paketleri. Avrupa, Orta Doğu ve Uzak Doğu'ya Ankara turları. Paris, Roma, Barcelona, Dubai destinasyonlarına uygun fiyatlı yurt dışı tur seçenekleri." />
      <meta name="keywords" content="Ankara yurt dışı tur, Avrupa turu Ankara, Paris turu, Roma turu, Dubai turu Ankara" />
      <link rel="canonical" href={`${siteUrl}/yurtdisi-turlar`} />
      <meta property="og:title" content="Yurt Dışı Turlar Ankara Çıkışlı | Ankara Travelers" />
      <meta property="og:description" content="Ankara çıkışlı Avrupa ve dünya tur paketleri. Paris, Roma, Dubai ve daha fazlası." />
      <meta property="og:url" content={`${siteUrl}/yurtdisi-turlar`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 h-64 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bf8c3e3c078e7e928f62bb6db41d7b5d.jpg"
            alt="Yurt Dışı Turlar"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-8 w-full">
          <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-flight-takeoff-line" /></div>
            <span>{yurtdisiTours.length} Tur</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Yurt Dışı Turlar</h1>
          <p className="text-gray-300 mt-1 text-sm">Ankara çıkışlı Avrupa ve dünya tur paketleri</p>
        </div>
      </section>

      {/* Euro notice */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-5 py-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-amber-500" />
          </div>
          <p className="text-amber-700 dark:text-amber-400 text-sm">
            Yurt dışı tur fiyatları <strong>Euro (€)</strong> cinsinden belirtilmiştir. Kesin fiyat için güncel kur bilgisini kontrol ediniz.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <div className="sticky top-24 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 dark:text-white text-sm">Filtreler</h3>
                <button onClick={resetFilters} className="text-xs text-orange-500 hover:text-orange-700 cursor-pointer">Sıfırla</button>
              </div>

              {/* Search */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-orange-500" /></div>
                  Tur Ara
                </h4>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400 text-xs" />
                  </div>
                  <input
                    type="text"
                    placeholder="Ara..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-400 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Price Range Euro */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-currency-line text-orange-500" /></div>
                  Fiyat Aralığı (€)
                </h4>
                <PriceRangeSlider
                  min={MIN_PRICE}
                  max={MAX_PRICE}
                  step={50}
                  value={priceRange}
                  onChange={setPriceRange}
                  formatLabel={v => `€${v.toLocaleString('tr-TR')}`}
                />
              </div>

              {/* Duration */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-orange-500" /></div>
                  Süre
                </h4>
                <div className="flex flex-col gap-2">
                  {DURATION_OPTIONS.map((d, i) => (
                    <button
                      key={i}
                      onClick={() => setDurationIdx(i)}
                      className={`text-left px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                        durationIdx === i ? 'bg-orange-500 text-white font-medium' : 'text-gray-600 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:text-orange-600'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-sort-asc text-orange-500" /></div>
                  Sırala
                </h4>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value as SortKey)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white text-gray-700 focus:outline-none focus:border-orange-400 cursor-pointer"
                >
                  <option value="default">Varsayılan</option>
                  <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                  <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                  <option value="name-asc">İsim: A-Z</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-800 dark:text-white">{filtered.length}</span> tur bulundu
              </span>
              <div className="hidden lg:flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Sırala:</span>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value as SortKey)}
                  className="px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white text-gray-700 focus:outline-none focus:border-orange-400 cursor-pointer"
                >
                  <option value="default">Varsayılan</option>
                  <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                  <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                  <option value="name-asc">İsim: A-Z</option>
                </select>
              </div>
            </div>

            {/* Mobile filters */}
            <div className="lg:hidden flex flex-wrap gap-2 mb-5">
              <div className="relative flex-1 min-w-32">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-xs" />
                </div>
                <input
                  type="text"
                  placeholder="Ara..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 bg-white"
                />
              </div>
              {DURATION_OPTIONS.map((d, i) => (
                <button
                  key={i}
                  onClick={() => setDurationIdx(i)}
                  className={`px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${
                    durationIdx === i ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-orange-50'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-flight-takeoff-line text-5xl text-gray-200" />
                </div>
                <p className="font-medium text-gray-500">Sonuç bulunamadı</p>
                <button onClick={resetFilters} className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-full text-sm cursor-pointer hover:bg-orange-600">
                  Filtreleri Temizle
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map(tour => <TourCard key={tour.id} tour={tour} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
